import { Property } from '@flatfile/api'

const appendAtTheEnd = false
// Helper function to insert additional fields at a specific position or at the end
const withFields = (
  fields: Property[],
  newFields: Array<{ key: string; type: string; label?: string }>,
  afterFieldKey?: string,
  beforeFieldKey?: string
): Property[] => {
  const actuallyNewFields = newFields.filter(
    (field) => !fields.find((f) => f.key === field.key)
  )
  const propertyFields: Property[] = actuallyNewFields.map((field) => ({
    ...field,
    type: field.type as any, // The type system will validate this at runtime
  }))

  if (appendAtTheEnd || (!afterFieldKey && !beforeFieldKey)) {
    return [...fields, ...propertyFields]
  }

  // If both before and after are specified, prioritize before
  if (beforeFieldKey) {
    const index = fields.findIndex((field) => field.key === beforeFieldKey)
    if (index === -1) {
      return [...fields, ...propertyFields]
    }
    return [
      ...fields.slice(0, index),
      ...propertyFields,
      ...fields.slice(index),
    ]
  }

  if (afterFieldKey) {
    const index = fields.findIndex((field) => field.key === afterFieldKey)
    if (index === -1) {
      return [...fields, ...propertyFields]
    }
    return [
      ...fields.slice(0, index + 1),
      ...propertyFields,
      ...fields.slice(index + 1),
    ]
  }

  return [...fields, ...propertyFields]
}

export interface ComputedSheetFields {
  columnConfig: Array<{
    value: string
    label: string
    type: string
    treatments: string[]
  }>
}

export function computeSheetFields(
  fields: Property[],
  rules: any[]
): ComputedSheetFields {
  let updatedFields = fields
  const enabledRules = (rules ?? []).filter((rule) => rule.disabled !== true)

  enabledRules.forEach((rule) => {
    if (!rule?.config?.type) {
      console.warn('rule has no type', rule)
      return
    }
    switch (rule.config.type) {
      case 'split': {
        const fieldsToAdd = rule.config.destinationFields
          .filter((fieldKey) => !fields.find((field) => field.key === fieldKey))
          .map((fieldKey) => ({
            key: fieldKey,
            type: 'string',
            label: fieldKey,
            treatments: ['ai-defined'],
          }))
        const anchorField =
          rule.config.sourceFields[rule.config.sourceFields.length - 1]
        updatedFields = withFields(updatedFields, fieldsToAdd, anchorField)
        break
      }
      case 'add-field': {
        const fieldsToAdd = [
          {
            key: rule.config.destinationField,
            type: rule.config.fieldType || rule.config.type || 'string',
            label: rule.config.destinationField,
          },
        ]
        const anchorField = rule.config.sourceField
        const beforeField =
          rule.config.position === 'left' ? anchorField : undefined
        const afterField =
          rule.config.position === 'right' ? anchorField : undefined
        updatedFields = withFields(
          updatedFields,
          fieldsToAdd,
          afterField,
          beforeField
        )
        break
      }
      case 'concatenate': {
        const fieldsToAdd = [
          {
            key: rule.config.destinationField,
            type: 'string',
            label: rule.config.destinationField,
            treatments: ['ai-defined'],
          },
        ]
        const anchorField =
          rule.config.sourceFields[rule.config.sourceFields.length - 1]
        updatedFields = withFields(updatedFields, fieldsToAdd, anchorField)
        break
      }
      case 'array': {
        const fieldsToAdd = [
          {
            key: rule.config.destinationField,
            type: 'array',
            label: rule.config.destinationField,
            treatments: ['ai-defined'],
          },
        ]
        const anchorField =
          rule.config.sourceFields[rule.config.sourceFields.length - 1]
        updatedFields = withFields(updatedFields, fieldsToAdd, anchorField)
        break
      }
      case 'normalize-list': {
        const fieldsToAdd = [
          {
            key: rule.config.destinationField,
            type: 'array',
            label: rule.config.destinationField,
            treatments: ['ai-defined'],
          },
        ]
        const anchorField = rule.config.sourceField
        updatedFields = withFields(updatedFields, fieldsToAdd, anchorField)
        break
      }
      case 'regex-extract': {
        const fieldsToAdd = rule.config.destinationFields
          .filter((fieldKey) => !fields.find((field) => field.key === fieldKey))
          .map((fieldKey) => ({
            key: fieldKey,
            type: 'string',
            label: fieldKey,
            treatments: ['ai-defined'],
          }))
        const anchorField = rule.config.sourceField
        updatedFields = withFields(updatedFields, fieldsToAdd, anchorField)
        break
      }
      case 'function': {
        const destinationFields = Array.isArray(rule.config.destinationFields)
          ? rule.config.destinationFields
          : [rule.config.destinationFields]
        const fieldsToAdd = destinationFields
          .filter((fieldKey) => !fields.find((field) => field.key === fieldKey))
          .map((fieldKey) => ({
            key: fieldKey,
            type: 'string',
            label: fieldKey,
            treatments: ['ai-defined'],
          }))
        const sourceFields = Array.isArray(rule.config.sourceFields)
          ? rule.config.sourceFields
          : [rule.config.sourceFields]
        const anchorField = sourceFields[sourceFields.length - 1]
        updatedFields = withFields(updatedFields, fieldsToAdd, anchorField)
        break
      }
      case 'coalesce': {
        const fieldsToAdd = [
          {
            key: rule.config.destinationField,
            type: 'string',
            label: rule.config.destinationField,
            treatments: ['ai-defined'],
          },
        ]
        const anchorField =
          rule.config.sourceFields[rule.config.sourceFields.length - 1]
        updatedFields = withFields(updatedFields, fieldsToAdd, anchorField)
        break
      }
      case 'interpolate': {
        const fieldsToAdd = [
          {
            key: rule.config.destinationField,
            type: 'string',
            label: rule.config.destinationField,
            treatments: ['ai-defined'],
          },
        ]
        const anchorField =
          rule.config.sourceFields[rule.config.sourceFields.length - 1]
        updatedFields = withFields(updatedFields, fieldsToAdd, anchorField)
        break
      }
      case 'arithmetic': {
        const fieldsToAdd = [
          {
            key: rule.config.destinationField,
            type: 'number',
            label: rule.config.destinationField,
            treatments: ['ai-defined'],
          },
        ]
        const anchorField =
          rule.config.sourceFields[rule.config.sourceFields.length - 1]
        updatedFields = withFields(updatedFields, fieldsToAdd, anchorField)
        break
      }
      case 'nest': {
        const fieldsToAdd = [
          {
            key: rule.config.destinationField,
            type: 'object',
            label: rule.config.destinationField,
            treatments: ['ai-defined'],
          },
        ]
        updatedFields = withFields(updatedFields, fieldsToAdd)
        break
      }
      case 'unpivot': {
        break
      }
      case 'apply-changes': {
        const fieldsToAdd = (rule.config.destinationFields || []).map(
          (field) => ({
            key: field,
            type: 'string',
            label: field,
            treatments: ['ai-defined'],
          })
        )
        updatedFields = withFields(updatedFields, fieldsToAdd)
        break
      }
      // Ignore rules that don't add new fields
      case 'assign':
      case 'constant':
      case 'transform':
      case 'delete':
      case 'ignore':
      case 'find-replace':
        break
      // Add support for change-field-type
      case 'change-field-type': {
        // Find the field and update its type if it exists
        updatedFields = updatedFields.map((field) =>
          field.key === rule.config.destinationField
            ? {
                ...field,
                type: rule.config.fieldType || rule.config.type || 'string',
              }
            : field
        )
        break
      }
    }
  })

  const columnConfig = updatedFields.map((field: Property) => ({
    value: field.key,
    label: field.label || field.key,
    type: field.type,
    treatments: field.treatments,
  }))

  return { columnConfig }
}
